/**
 * Read a Meridian `.tar.gz` bundle **locally**, in the operator's browser.
 *
 * Pure, React-free, and — this is the point — **entirely client-side**. Parsing
 * happens on the file the operator picked, with no request at all. Reading a
 * bundle a partner site handed you must not be the same act as installing it,
 * and neither import route has a dry-run: they unpack and install. So a preview
 * cannot be a server round trip, and making it one would mean either a new
 * endpoint or an install-then-report flow that writes first and asks second.
 *
 * # Shared by both bundle surfaces, deliberately
 *
 * Peer bundles (ADR-039) and provider bundles (ADR-040 §D-3) are **the same
 * archive format** — ``MANIFEST.json`` plus one definition document plus docs —
 * and differ only in which member carries the definition and what key it nests
 * under. Everything below the definition is format machinery: gzip inflation,
 * tar framing, digest re-computation and ``${token}`` scanning. One definition
 * of it, two consumers (CLAUDE.md §4.4); the direction-specific reading lives in
 * :file:`@/peers/peer-bundle-preview` and :file:`@/providers/provider-bundle-preview`.
 *
 * # No new dependency, deliberately
 *
 * A `.tar.gz` needs gzip inflation and tar framing, and the obvious reach is for
 * `pako` + a tar library. Both are avoidable: `DecompressionStream("gzip")` and
 * `crypto.subtle.digest` are platform APIs, and tar's header is a fixed 512-byte
 * record with octal fields — the parser below is the whole format this needs.
 * A UI dependency is a CLAUDE.md §4.3 cascade (tech-stack + LICENSING + operator
 * confirmation) and an air-gap surface, for about eighty lines of arithmetic.
 *
 * Cross-references:
 *   - REQ-UI-150 / REQ-UI-157 (show what was parsed before anything is written).
 *   - Feature-spec D-15 (the manifest digest is what an installer checks).
 */

/** A bundle this parser cannot render honestly. Its ``message`` is operator-facing. */
export class BundleUnreadable extends Error {
  constructor(message: string) {
    super(message);
    this.name = "BundleUnreadable";
  }
}

/** Bound on what will be read into memory — mirrors the BFF's own archive ceiling. */
export const MAX_UNCOMPRESSED_BYTES = 32 * 1024 * 1024;
/** tar's fixed record size. Every header and every payload is padded to a multiple of it. */
const TAR_BLOCK = 512;
/** Matches ``${token}`` exactly as ``unresolved_placeholders`` does server-side. */
const TOKEN_PATTERN = /\$\{([A-Za-z_][A-Za-z0-9_]*)\}/g;

/**
 * Inflate a gzip stream using the platform decompressor.
 *
 * Written against ``ReadableStream`` rather than ``Blob.stream()`` on purpose:
 * jsdom implements the former and not the latter, so this shape is exercised by
 * the unit tests rather than only in a browser.
 *
 * @throws BundleUnreadable when the browser has no decompressor, the bytes are
 *   not gzip, or the inflated archive exceeds the bundle ceiling.
 */
export async function gunzip(bytes: ArrayBuffer): Promise<Uint8Array> {
  if (typeof DecompressionStream !== "function") {
    throw new BundleUnreadable(
      "This browser cannot decompress the archive. Install the bundle from the deployment's staging directory instead.",
    );
  }
  // Typed `BufferSource` to match `DecompressionStream.writable`, which accepts
  // any view; a `ReadableStream<Uint8Array>` will not pipe into it under
  // `strict`.
  const source = new ReadableStream<BufferSource>({
    start(controller) {
      controller.enqueue(new Uint8Array(bytes));
      controller.close();
    },
  });
  try {
    const inflated = source.pipeThrough(new DecompressionStream("gzip"));
    const buffer = await new Response(inflated).arrayBuffer();
    if (buffer.byteLength > MAX_UNCOMPRESSED_BYTES) {
      throw new BundleUnreadable("This archive is larger than a bundle is allowed to be.");
    }
    return new Uint8Array(buffer);
  } catch (caught) {
    if (caught instanceof BundleUnreadable) throw caught;
    throw new BundleUnreadable("This file is not a gzip archive.");
  }
}

/** Read a NUL-terminated ASCII field out of a tar header. */
function field(block: Uint8Array, offset: number, length: number): string {
  const slice = block.subarray(offset, offset + length);
  const end = slice.indexOf(0);
  return new TextDecoder().decode(end === -1 ? slice : slice.subarray(0, end)).trim();
}

/**
 * Split a tar stream into its regular-file members.
 *
 * Directories, symlinks and the GNU/PAX extension records are skipped rather
 * than rejected: a valid bundle may legitimately carry directory entries, and a
 * parser that refused them would reject archives the BFF installs happily —
 * which would be this preview inventing a rule the system does not have.
 *
 * @throws BundleUnreadable on a malformed table of contents or an empty archive.
 */
export function untar(bytes: Uint8Array): Map<string, Uint8Array> {
  const members = new Map<string, Uint8Array>();
  let offset = 0;
  while (offset + TAR_BLOCK <= bytes.length) {
    const header = bytes.subarray(offset, offset + TAR_BLOCK);
    // Two consecutive zero blocks end the archive; one is enough to stop here.
    if (header.every((b) => b === 0)) break;
    const name = field(header, 0, 100);
    const prefix = field(header, 345, 155);
    const sizeOctal = field(header, 124, 12);
    // ⚠ Offset 156, NOT 157. The header is name[100] mode[8] uid[8] gid[8]
    // size[12] mtime[12] chksum[8] typeflag[1], which lands typeflag at 156 and
    // linkname at 157. Reading 157 finds linkname's first byte — NUL on almost
    // every entry — so every member, including directories and symlinks, would
    // classify as a regular file and the bug would hide behind archives that
    // happen to contain only files.
    const typeFlag = String.fromCharCode(header[156] ?? 0);
    const size = Number.parseInt(sizeOctal === "" ? "0" : sizeOctal, 8);
    if (!Number.isFinite(size) || size < 0) {
      throw new BundleUnreadable("This archive's table of contents is malformed.");
    }
    offset += TAR_BLOCK;
    // "0" and NUL both mean a regular file; "x"/"g"/"L" are metadata records.
    if (typeFlag === "0" || typeFlag === "\0") {
      const path = prefix === "" ? name : `${prefix}/${name}`;
      // `tar -czf b.tar.gz -C dir .` writes every member as `./MANIFEST.json`,
      // which the BFF's extractor flattens to the same place a bare
      // `MANIFEST.json` lands. Stripping the prefix here keeps the preview
      // reading exactly the archives the server accepts — no more (a member
      // under a real subdirectory keeps its path and is looked up as written,
      // which is what makes the nested-root case below detectable).
      const normalized = path.startsWith("./") ? path.slice(2) : path;
      if (normalized !== "") members.set(normalized, bytes.subarray(offset, offset + size));
    }
    // Payloads are padded up to the next block boundary.
    offset += Math.ceil(size / TAR_BLOCK) * TAR_BLOCK;
  }
  if (members.size === 0) throw new BundleUnreadable("This archive contains no files.");
  return members;
}

/** Lowercase hex SHA-256 of some bytes, matching the manifest's own encoding. */
export async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", bytes as BufferSource);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * Read one JSON member out of an unpacked archive.
 *
 * @param members The unpacked archive.
 * @param member The path to read, e.g. ``"MANIFEST.json"``.
 * @param bundleNoun What kind of bundle this was expected to be, for the copy —
 *   ``"peer"`` or ``"provider"``. The message is shown to an operator verbatim,
 *   and "this is not a bundle" is less useful than naming which one was expected.
 * @throws BundleUnreadable when the member is absent or is not valid JSON.
 */
export function readJsonMember(
  members: Map<string, Uint8Array>,
  member: string,
  bundleNoun: string,
): unknown {
  const bytes = members.get(member);
  if (bytes === undefined) {
    // ⚠ The commonest operator mistake, and it is worth naming rather than
    // reporting as a generic "not a bundle": `tar -czf b.tar.gz mybundle/`
    // wraps every member in a directory. The BFF extracts an archive flat and
    // treats what lands as the bundle root, so a wrapped archive is refused
    // there too — this preview must not be the more permissive of the two, or
    // it would say "looks fine" about a file the very next click rejects.
    const nested = [...members.keys()].find((path) => path.endsWith(`/${member}`));
    if (nested !== undefined) {
      throw new BundleUnreadable(
        `This archive wraps the bundle in a directory (${nested}). Re-create it from inside the ` +
          `bundle directory — \`tar -czf bundle.tar.gz -C <dir> .\` — so ${member} is at the top level.`,
      );
    }
    throw new BundleUnreadable(
      `This archive has no ${member}, so it is not a ${bundleNoun} bundle.`,
    );
  }
  try {
    return JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    throw new BundleUnreadable(`${member} in this archive is not valid JSON.`);
  }
}

/** Collect distinct ``${token}`` names in first-seen order — keys as well as values. */
export function collectTokens(value: unknown, found: string[] = []): string[] {
  if (typeof value === "string") {
    for (const match of value.matchAll(TOKEN_PATTERN)) {
      if (!found.includes(match[1])) found.push(match[1]);
    }
  } else if (Array.isArray(value)) {
    for (const item of value) collectTokens(item, found);
  } else if (typeof value === "object" && value !== null) {
    for (const [key, item] of Object.entries(value)) {
      collectTokens(key, found);
      collectTokens(item, found);
    }
  }
  return found;
}

/** Narrow an unknown to a plain object, or ``{}`` — the shape every reader wants. */
export function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

/** Narrow an unknown to a string, or ``null``. */
export function asString(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

/** One archive member, as the manifest declares it and the bytes bear it out. */
export interface BundleArtifact {
  /** Path within the archive, e.g. ``provider.json``. */
  member: string;
  /** The manifest's declared kind — ``definition`` / ``doc`` / ``conversion_spec`` / … */
  kind: string;
  /** The digest the manifest declares (D-15: what an installer checks). */
  declaredSha256: string;
  /**
   * The digest of the bytes actually present, or ``null`` when the member the
   * manifest names is absent from the archive.
   *
   * Kept separate from the declared value rather than reduced to a boolean: an
   * operator chasing a mismatch needs to see both, and "absent" is a different
   * fault from "different bytes".
   */
  actualSha256: string | null;
}

/** A placeholder the bundle ships with, and whether the operator has answered it. */
export interface BundlePlaceholder {
  token: string;
  kind: string;
  /** The manifest's own question, shown verbatim — it is what the author wrote to ask. */
  prompt: string;
  /** JSON pointers into the definition, from the manifest. */
  pointers: string[];
  /** ``true`` when ``${token}`` is still literally present in a document. */
  unresolved: boolean;
}

/** What {@link readManifest} resolved out of ``MANIFEST.json``. */
export interface ManifestReading {
  bundleName: string;
  generatedBy: string;
  artifacts: BundleArtifact[];
  placeholders: BundlePlaceholder[];
  /**
   * Members whose bytes do not match the manifest, as operator-facing sentences.
   *
   * Separate from the unresolved tokens because they call for different acts: an
   * unresolved placeholder is the operator's to fill in, a digest mismatch means
   * the archive is not the one that was signed and is nobody's to fix locally.
   */
  digestProblems: string[];
}

/**
 * Read ``MANIFEST.json`` — the artifact inventory, the digest check, and the
 * placeholder questions resolved against the definition's remaining tokens.
 *
 * @param manifest The parsed manifest document.
 * @param members The unpacked archive, so each declared digest can be re-computed.
 * @param unresolvedTokens Tokens still literally present in the definition.
 */
export async function readManifest(
  manifest: Record<string, unknown>,
  members: Map<string, Uint8Array>,
  unresolvedTokens: readonly string[],
): Promise<ManifestReading> {
  const declared = Array.isArray(manifest.artifacts) ? manifest.artifacts : [];
  const artifacts: BundleArtifact[] = [];
  const digestProblems: string[] = [];
  for (const entry of declared) {
    const record = asRecord(entry);
    const member = asString(record.member);
    if (member === null) continue;
    const bytes = members.get(member);
    const actual = bytes === undefined ? null : await sha256Hex(bytes);
    const declaredSha256 = asString(record.sha256) ?? "";
    artifacts.push({
      member,
      kind: asString(record.kind) ?? "unknown",
      declaredSha256,
      actualSha256: actual,
    });
    // D-15 makes the digest the thing an installer checks, so a mismatch here is
    // what the BFF will refuse — said now, in the operator's terms, rather than
    // surfacing later as an opaque rejection.
    if (actual === null) {
      digestProblems.push(`${member} is listed in the manifest but missing from the archive.`);
    } else if (declaredSha256 !== "" && actual !== declaredSha256) {
      digestProblems.push(`${member} does not match the digest its manifest declares.`);
    }
  }
  const placeholders: BundlePlaceholder[] = (
    Array.isArray(manifest.placeholders) ? manifest.placeholders : []
  ).map((entry) => {
    const record = asRecord(entry);
    const token = asString(record.token) ?? "";
    return {
      token,
      kind: asString(record.kind) ?? "value",
      prompt: asString(record.prompt) ?? "",
      pointers: Array.isArray(record.pointers)
        ? record.pointers.filter((p): p is string => typeof p === "string")
        : [],
      unresolved: unresolvedTokens.includes(token),
    };
  });
  return {
    bundleName: asString(manifest.bundle_name) ?? "(unnamed bundle)",
    generatedBy: asString(manifest.generated_by) ?? "",
    artifacts,
    placeholders,
    digestProblems,
  };
}
